import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface CustomResponse<T = any> {
  error?: string;
  data?: T;
  success?: boolean;
}

export interface OneFiDirectTransactionRequest {
  merchant_store_id?: string;
  merchant_store_id_type?: string;
  product_variant_id?: string;
  product_variant_id_type?: string;
  emi_plan_id?: string;
  order_amount?: number;
  mobile?: string;
  pan?: string;
  unique_transaction_id?: string;
  order_note?: string;
}

export interface OneFiDirectAmount {
  order_amount: number;
  down_payment: number;
  processing_fee: number;
  loan_amount: number;
  emi: number;
  tenure_months: number;
  interest_rate?: number;
}

export interface OneFiDirectTransactionData {
  transaction_id: string;
  order_number?: string;
  selected_emi_plan_id?: string;
  amount?: OneFiDirectAmount;
  can_continue_with_otp: boolean;
}

export interface OneFiSendOtpData {
  transaction_id: string;
  mobile?: string;
  expires_at?: string;
  resend_available_at?: string;
}

export interface OneFiConfirmOtpRequest {
  otp: string;
}

export interface OneFiConfirmOtpData {
  transaction_id: string;
  loan_number?: string;
  loan_status?: string;
  order_number?: string;
  order_confirmed?: boolean;
}

export interface OneFiHostedTransactionData {
  transaction_id: string;
  payment_url: string;
  expires_at?: string;
}

@Injectable({
  providedIn: 'root'
})
export class OneFiService {
  baseUrl: string = '';

  constructor(private httpClient: HttpClient) {
    this.baseUrl = environment.apiUrl;
  }

  /**
   * Flow A - Start Direct Transaction
   * POST api/1fi/direct
   */
  startDirectTransaction(request: Partial<OneFiDirectTransactionRequest> = {}): Observable<CustomResponse<OneFiDirectTransactionData>> {
    return this.httpClient.post<CustomResponse<OneFiDirectTransactionData>>(`${this.baseUrl}1fi/direct`, request);
  }

  /**
   * Flow A - Send OTP
   * POST api/1fi/{transactionId}/otp
   */
  sendOtp(transactionId: string): Observable<CustomResponse<OneFiSendOtpData>> {
    return this.httpClient.post<CustomResponse<OneFiSendOtpData>>(`${this.baseUrl}1fi/${encodeURIComponent(transactionId)}/otp`, {});
  }

  /**
   * Flow A - Confirm OTP
   * POST api/1fi/{transactionId}/confirm
   */
  confirmOtp(transactionId: string, otp: string): Observable<CustomResponse<OneFiConfirmOtpData>> {
    return this.httpClient.post<CustomResponse<OneFiConfirmOtpData>>(`${this.baseUrl}1fi/${encodeURIComponent(transactionId)}/confirm`, { otp });
  }

  /**
   * Flow B - Start Hosted Transaction
   * POST api/1fi/hosted
   */
  createHostedTransaction(request: any = {}): Observable<CustomResponse<OneFiHostedTransactionData>> {
    return this.httpClient.post<CustomResponse<OneFiHostedTransactionData>>(`${this.baseUrl}1fi/hosted`, request);
  }

  /**
   * Get Transaction Details
   * GET api/1fi/{transactionId}
   */
  getTransactionDetails(transactionId: string): Observable<CustomResponse<any>> {
    return this.httpClient.get<CustomResponse<any>>(`${this.baseUrl}1fi/${encodeURIComponent(transactionId)}`);
  }

  /**
   * Check Transaction Status & Sync Details
   * GET api/1fi/status or GET api/1fi/status/{transactionId}
   */
  checkTransactionStatus(transactionId?: string): Observable<CustomResponse<any>> {
    const url = transactionId && transactionId.trim()
      ? `${this.baseUrl}1fi/status/${encodeURIComponent(transactionId.trim())}`
      : `${this.baseUrl}1fi/status`;
    return this.httpClient.get<CustomResponse<any>>(url);
  }

  /**
   * Check Eligibility
   * GET api/1fi/eligibility
   */
  checkEligibility(params?: any): Observable<CustomResponse<any>> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.httpClient.get<CustomResponse<any>>(`${this.baseUrl}1fi/eligibility`, { params: httpParams });
  }

  /**
   * Get EMI Plan
   * GET api/1fi/emi-plan
   */
  getEmiPlan(params?: any): Observable<CustomResponse<any>> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.httpClient.get<CustomResponse<any>>(`${this.baseUrl}1fi/emi-plan`, { params: httpParams });
  }
}
